import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, Edit3, Send, Download, Plus, Search, Link as LinkIcon, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { API, getToken } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return s;
  }
}

export default function ContractsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/contracts");
      setRows(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function sendContract(c) {
    try {
      const { data } = await api.post(`/contracts/${c.id}/send`);
      const link = `${window.location.origin}/c/${data.public_token}`;
      try {
        await navigator.clipboard.writeText(link);
      } catch {}
      toast.success("Contract marked as Sent. Link copied to clipboard.");
      load();
    } catch (e) {
      toast.error("Failed to send contract");
    }
  }

  async function copyLink(c) {
    const link = `${window.location.origin}/c/${c.public_token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  }

  async function downloadPdf(c) {
    try {
      const res = await fetch(`${API}/contracts/${c.id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sheesh-contract-${(c.client_name || "client").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download PDF");
    }
  }

  const filtered = rows.filter(
    (r) =>
      r.client_name?.toLowerCase().includes(q.toLowerCase()) ||
      r.event_location?.toLowerCase().includes(q.toLowerCase()) ||
      r.status?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="contracts-list-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Records</div>
          <h1 className="font-serif-brand text-4xl sm:text-5xl tracking-tight">Contracts</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search clients, status…"
              data-testid="contracts-search"
              className="bg-[#0e0e0e] border border-white/10 pl-9 pr-3 py-2 text-sm focus:border-[#C9963F] focus:outline-none w-56"
            />
          </div>
          <Link
            to="/admin/contracts/new"
            data-testid="new-contract-btn-list"
            className="inline-flex items-center gap-2 bg-[#C9963F] text-black px-4 py-2 text-sm font-medium hover:bg-[#A67A2D] transition-colors"
          >
            <Plus size={16} /> New
          </Link>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && <div className="text-white/40 text-sm">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-white/40 text-sm border border-white/10 p-8 text-center">No contracts yet</div>
        )}
        {filtered.map((c) => (
          <div key={c.id} className="bg-[#0e0e0e] border border-white/10 p-4" data-testid={`contract-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="text-white font-medium truncate">{c.client_name}</div>
                <div className="text-xs text-white/50 mt-0.5">{fmtDate(c.event_date)}</div>
              </div>
              <StatusBadge status={c.status} />
            </div>
            <div className="text-xs text-white/40 mt-2 truncate">{c.event_location || "—"}</div>
            <div className="text-sm text-[#C9963F] mt-2">${(c.total ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            <div className="text-xs mt-1">
              {Number(c.amount_outstanding || 0) === 0 ? (
                <span className="text-[#C9963F]">● Paid in full</span>
              ) : (
                <span className="text-amber-300">Outstanding: ${Number(c.amount_outstanding || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button onClick={() => navigate(`/admin/contracts/${c.id}`)} className="px-3 py-1.5 text-xs border border-white/15 hover:border-[#C9963F] hover:text-[#C9963F]" data-testid={`view-${c.id}`}>
                <Eye size={12} className="inline mr-1" /> View
              </button>
              <button onClick={() => sendContract(c)} className="px-3 py-1.5 text-xs border border-[#C9963F]/40 text-[#C9963F] hover:bg-[#C9963F]/10" data-testid={`send-${c.id}`}>
                <Send size={12} className="inline mr-1" /> Send
              </button>
              <button onClick={() => copyLink(c)} className="px-3 py-1.5 text-xs border border-white/15 hover:border-white/40" data-testid={`link-${c.id}`}>
                <LinkIcon size={12} className="inline mr-1" /> Link
              </button>
              <button onClick={() => downloadPdf(c)} className="px-3 py-1.5 text-xs border border-white/15 hover:border-white/40" data-testid={`pdf-${c.id}`}>
                <Download size={12} className="inline mr-1" /> PDF
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-[#0e0e0e] border border-white/10">
        <table className="w-full text-sm" data-testid="contracts-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-white/50 border-b border-white/10">
              <th className="px-5 py-3 font-medium">Client</th>
              <th className="px-5 py-3 font-medium">Event Date</th>
              <th className="px-5 py-3 font-medium">Location</th>
              <th className="px-5 py-3 font-medium">Total</th>
              <th className="px-5 py-3 font-medium">Outstanding</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-center text-white/40">Loading…</td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-center text-white/40">No contracts yet</td>
              </tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`contract-row-${c.id}`}>
                <td className="px-5 py-4">
                  <div className="text-white font-medium">{c.client_name}</div>
                  <div className="text-xs text-white/40">{c.client_email || c.client_phone || "—"}</div>
                </td>
                <td className="px-5 py-4 text-white/70">{fmtDate(c.event_date)}</td>
                <td className="px-5 py-4 text-white/60 max-w-xs truncate">{c.event_location || "—"}</td>
                <td className="px-5 py-4 text-[#C9963F]">${(c.total ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="px-5 py-4">
                  {Number(c.amount_outstanding || 0) === 0 ? (
                    <span className="text-[10px] uppercase tracking-wider text-[#C9963F]">● Paid in full</span>
                  ) : (
                    <span className="text-amber-300 text-sm">${Number(c.amount_outstanding || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  )}
                </td>
                <td className="px-5 py-4"><StatusBadge status={c.status} /></td>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-1.5 justify-end">
                    <button onClick={() => navigate(`/admin/contracts/${c.id}`)} title="View / Edit" data-testid={`view-${c.id}`}
                      className="p-2 text-white/60 hover:text-[#C9963F] transition-colors">
                      <Edit3 size={14} />
                    </button>
                    <button onClick={() => sendContract(c)} title="Send" data-testid={`send-${c.id}`}
                      className="p-2 text-white/60 hover:text-[#C9963F] transition-colors">
                      <Send size={14} />
                    </button>
                    <button onClick={() => copyLink(c)} title="Copy customer link" data-testid={`link-${c.id}`}
                      className="p-2 text-white/60 hover:text-[#C9963F] transition-colors">
                      <Copy size={14} />
                    </button>
                    <button onClick={() => downloadPdf(c)} title="Download PDF" data-testid={`pdf-${c.id}`}
                      className="p-2 text-white/60 hover:text-[#C9963F] transition-colors">
                      <Download size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
